import type { JSX } from "react";
import { Badge, type BadgeProps } from "@linkhr/design-system";
import { metaSeparator, useTranslation } from "@linkhr/i18n";
import { codeLabel } from "./codeLabel";

type Tone = NonNullable<BadgeProps["variant"]>;

/** The tone of each state, so colour is never the only signal (the label is always there). */
const TONES = {
  intake: {
    PROCESSING: "info",
    NEEDS_REVIEW: "warning",
    READY: "success",
    WAITING_ON_OTHERS: "info",
    QUARANTINED: "danger",
    SCAN_FAILED: "danger",
    UNREADABLE: "danger",
    DUPLICATE_BLOCKED: "danger",
    POSSIBLE_DUPLICATE: "warning",
    POSTED: "neutral",
    REJECTED: "neutral",
  },
  period: { OPEN: "success", SOFT_CLOSED: "warning", HARD_CLOSED: "danger" },
  evidence: { EINVOICE_PRESENT: "success", EINVOICE_MISSING: "warning", NOT_APPLICABLE: "neutral" },
  qr: { DECODED: "neutral", STRUCTURALLY_VALID: "info", CROSS_CHECKED: "success" },
  document: {
    DRAFT: "neutral",
    IN_REVIEW: "info",
    CHANGES_REQUESTED: "warning",
    APPROVED: "success",
    POSTED: "neutral",
    VOID_BEFORE_POSTING: "neutral",
  },
  settlement: { UNPAID: "warning", PARTLY_SETTLED: "info", SETTLED: "success", OVERDUE: "danger" },
  payment: {
    DRAFT: "neutral",
    APPROVED: "info",
    RELEASED: "primary",
    SETTLED: "success",
    FAILED: "danger",
    UNKNOWN: "warning",
  },
  recon: {
    SUGGESTED_EXACT: "success",
    NEEDS_REVIEW: "warning",
    NO_SUGGESTION: "neutral",
    DEFERRED: "neutral",
    MATCHED: "success",
    PENDING_AT_BANK: "info",
  },
  journal: { DRAFT: "neutral", POSTED: "success", REVERSED: "neutral" },
  fiscal: {
    REQUIRED: "neutral",
    PREPARED: "info",
    SEALED: "info",
    QUEUED: "info",
    SUBMITTED: "info",
    ACCEPTED: "success",
    ACCEPTED_WITH_WARNINGS: "warning",
    REJECTED: "danger",
    RETRYING: "warning",
    UNKNOWN_OUTCOME: "warning",
    NOT_ACTIVATED: "neutral",
  },
  fiscal_group: {
    NOT_SENT: "neutral",
    SENDING: "info",
    ACCEPTED: "success",
    NEEDS_FIX: "danger",
    WAITING: "warning",
  },
  cheque: {
    IN_SAFE: "info",
    UNDER_COLLECTION: "info",
    CLEARED: "success",
    RETURNED: "danger",
    ISSUED: "neutral",
    PRESENTED: "info",
    CANCELLED: "neutral",
  },
  // The real supplier documents (the `document` family above is the prototype's).
  bill: {
    DRAFT: "neutral",
    IN_REVIEW: "info",
    CHANGES_REQUESTED: "warning",
    POSTED: "success",
    REJECTED: "neutral",
    VOID_BEFORE_POSTING: "neutral",
    REVERSED: "neutral",
  },
  tax_invoice: { PRESENT: "success", MISSING: "warning", NOT_REQUIRED: "neutral" },
  payee_change: {
    PROPOSED: "info",
    APPROVED: "success",
    REJECTED: "neutral",
    WITHDRAWN: "neutral",
  },
  work_item: { OPEN: "info", RESOLVED: "success" },
  // The real supplier payments (the `payment` family above is the prototype's).
  supplier_payment: {
    DRAFT: "neutral",
    APPROVED: "info",
    RELEASED: "primary",
    CLEARED: "success",
    VOIDED: "neutral",
    REVERSED: "neutral",
  },
  // Worded per view: `state.bank_movement.<VALUE>.{business,accountant}`.
  bank_movement: {
    DRAFT: "neutral",
    SENT: "info",
    POSTED: "success",
    VOIDED: "neutral",
    REVERSED: "neutral",
  },
} as const satisfies Record<string, Record<string, Tone>>;

export type StateFamily = keyof typeof TONES;

/** The families worded per view take the view; the others never do. */
type Family =
  | { family: "bank_movement"; view: "business" | "accountant" }
  | { family: Exclude<StateFamily, "bank_movement">; view?: undefined };

/**
 * A state as a labelled chip: `state.<family>.<VALUE>` (`.<view>` for a family worded per
 * view; or `label`, a screen's own word for it), plus an optional detail.
 */
export function StateChip({
  family,
  view,
  value,
  detail,
  label: own,
}: Family & {
  value: string;
  detail?: string;
  label?: string;
}): JSX.Element {
  const { t, i18n } = useTranslation("accounting");
  const tone: Tone = (TONES[family] as Record<string, Tone>)[value] ?? "neutral";
  const key = `accounting:state.${family}.${value}${view !== undefined ? `.${view}` : ""}`;
  const label = own ?? codeLabel(i18n, t, key, value);
  return (
    <Badge variant={tone}>
      {label}
      {detail !== undefined ? `${metaSeparator(i18n.language)}${detail}` : null}
    </Badge>
  );
}
